import { ReactNode, useCallback, useEffect, useState } from "react";
import { AutovioCalendarEvent } from "../model/autovioCalendarEvents";
import { useGetOne } from "react-admin";
import { EventDialog } from "../dialogs/EventDialog";

interface UseEventDialogParams {
  /**
   * If not specified or `true`, the browser URL is updated whenever an event dialog is opened or closed.
   * Furthermore, if this hook is called for the first time and the browser URL contains the search parameter
   * openDialog with a valid event id, the event dialog for that event is automatically opened.
   */
  syncWithUrl?: boolean;
  /** Called when the event dialog is closed. */
  onClose?: () => void;
}

interface UseEventDialogResult {
  /** Function to open the event dialog for a given event or event ID. */
  openEventDialog: (eventOrEventId: AutovioCalendarEvent | string) => false;
  /** The event dialog component, which must included in the React element tree. */
  eventDialog: ReactNode;
}

export function useEventDialog({ syncWithUrl, onClose }: UseEventDialogParams = {}): UseEventDialogResult {
  const [eventId, setEventId] = useState<string>("");
  const { data: event } = useGetOne<AutovioCalendarEvent>("calendarEvents", { id: eventId }, { enabled: !!eventId });
  // Side effect: automatically open event dialog if browser URL contains openDialog=...
  useEffect(() => {
    if (syncWithUrl) {
      const openDialogValue = new URL(location.href).searchParams.get("openDialog");
      if (openDialogValue) {
        const i = openDialogValue.indexOf("!");
        if (i > 0) {
          const eventId = openDialogValue.substring(i + 1);
          if (eventId) {
            setEventId(eventId);
          }
        }
      }
    }
  }, [syncWithUrl]);
  // Side effect: update browser URL when the event dialog is shown ...
  useEffect(() => {
    if (syncWithUrl && event) {
      const url = new URL(location.href);
      const openDialogValue = `${event.type}Dialog!${event.id}`;
      if (url.searchParams.get("openDialog") !== openDialogValue) {
        url.searchParams.set("openDialog", openDialogValue);
        browserHistory.replace(url.toString());
      }
    }
  }, [syncWithUrl, event]);
  const openEventDialog = useCallback((eventOrEventId: AutovioCalendarEvent | string) => {
    const eventId = typeof eventOrEventId === "string" ? eventOrEventId : eventOrEventId.id;
    setEventId(eventId);
    return false;
  }, []);
  const closeEventDialog = useCallback(() => {
    setEventId("");
    if (syncWithUrl) {
      const url = new URL(location.href);
      url.searchParams.delete("openDialog");
      url.searchParams.delete("showHistory");
      browserHistory.replace(url.toString());
    }
    onClose?.();
  }, [syncWithUrl, onClose]);
  const eventDialog = event ? <EventDialog key={event.id} isOpen event={event} onClose={closeEventDialog} /> : null;
  return { openEventDialog, eventDialog };
}
